import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { Plus, Trash2, ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';

export default function NotesView({ user, onDateChange }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [note, setNote] = useState('');
  const [noteId, setNoteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    loadNote();
  }, [currentDate, user]);

  const loadNote = async () => {
    setLoading(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const q = query(
        collection(db, 'notes'),
        where('userId', '==', user.uid),
        where('date', '==', dateStr)
      );
      const snapshot = await getDocs(q);
      if (snapshot.docs.length > 0) {
        const noteDoc = snapshot.docs[0];
        setNote(noteDoc.data().content || '');
        setNoteId(noteDoc.id);
      } else {
        setNote('');
        setNoteId(null);
      }
    } catch (error) {
      console.error('Error loading note:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async () => {
    if (!note.trim() && !noteId) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      if (noteId) {
        await updateDoc(doc(db, 'notes', noteId), {
          content: note,
          updatedAt: serverTimestamp()
        });
      } else if (note.trim()) {
        const docRef = await addDoc(collection(db, 'notes'), {
          userId: user.uid,
          date: dateStr,
          content: note,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setNoteId(docRef.id);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteNote = async () => {
    if (!noteId) return;
    setIsSaving(true);
    try {
      await deleteDoc(doc(db, 'notes', noteId));
      setNote('');
      setNoteId(null);
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date) => format(date, 'EEEE, MMMM d, yyyy');

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-slate-800">Daily Journal</h1>
            <div className="text-right">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Date</p>
              <p className="text-sm font-semibold text-slate-700">{formatDate(currentDate)}</p>
            </div>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => { const d = subDays(currentDate, 1); setCurrentDate(d); onDateChange?.(d); }}
              disabled={loading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronLeft size={20} className="text-slate-600" />
            </button>

            <button
              onClick={() => { const d = new Date(); setCurrentDate(d); onDateChange?.(d); }}
              disabled={loading}
              style={{ backgroundColor: 'var(--color-primary)' }}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            >
              Today
            </button>

            <button
              onClick={() => { const d = addDays(currentDate, 1); setCurrentDate(d); onDateChange?.(d); }}
              disabled={loading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronRight size={20} className="text-slate-600" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-secondary)' }}></div>
            <p className="text-sm text-emerald-700">Note saved successfully</p>
          </div>
        )}

        {/* Notes Area */}
        {loading ? (
          <div className="text-center py-12">
            <Loader size={32} className="mx-auto animate-spin" style={{ color: 'var(--color-primary)' }} />
            <p className="mt-3 text-slate-600">Loading your note...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isSaving}
              placeholder="Write your thoughts, reflections, or journal entry for today..."
              className="w-full h-80 p-4 border border-slate-300 rounded-lg focus:outline-none resize-none text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
            />

            <div className="flex gap-2 justify-end">
              {noteId && (
                <button
                  onClick={deleteNote}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium bg-red-100 hover:bg-red-200 disabled:bg-slate-100 text-red-700 disabled:text-slate-600 rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
                >
                  {isSaving ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Delete
                </button>
              )}
              <button
                onClick={saveNote}
                disabled={isSaving}
                style={{ backgroundColor: 'var(--color-primary)' }}
                className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-primary-light)', borderColor: 'var(--color-primary)' + '33' }}>
        <p className="text-sm text-slate-800">
          💭 <strong>Journaling Tip:</strong> Use this space to reflect on your day, capture insights from your daily goals, and record events worth remembering.
        </p>
      </div>
    </div>
  );
}
